import { useCallback, useEffect, useState, type FormEvent } from 'react'

import {
  ApiError,
  api,
  can,
  refusalReason,
  type Probe,
  type RegisterInput,
  type RegisterKind,
  type RegisterOwnership,
  type RegisterReceipt,
  type RiskRegister,
} from '../../lib/api'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Status, Toggletip, type Tone } from '../../components/kit'
import { Icon } from '../../components/ui'
import '../../styles/sweep.css'

// The risk registers this tenant connected, in one place.
//
// # WHAT THIS SCREEN HAS TO ANSWER
//
// Four questions somebody asks about a register, in the order they ask them:
// is it connected and working, what does it get from us and what does it decide
// itself, when did anything last go each way, and if something is wrong, what.
// Sending and hearing back are shown apart because they fail apart: a register
// can take every risk and never tell us about a single decision.
//
// # WHY THE CARDS UNDER INTEGRATIONS COME HERE
//
// A register used to be configurable in two places that did not know about
// each other, and one of them sent nothing. There is one record now, and this
// is where it is kept. The catalogue cards open this screen.
//
// # WHY STATES ARE LABELS AND REASONS OPEN FROM AN (i)
//
// A card is read for four answers, so each answer is a label or a count and
// the sentence behind it opens beside it. What the API says went wrong stays
// on the card word for word, because that is the thing somebody came to read.

const KIND_WORD: Record<RegisterKind, string> = {
  FOCTTA_CORE: 'FOCTTA Core',
  GENERIC_WEBHOOK: 'Another register',
  SERVICENOW_IRM: 'ServiceNow',
}

const STATE_WORD: Record<string, { word: string; tone: Tone }> = {
  PAUSED: { word: 'Paused', tone: 'neutral' },
  FAILING: { word: 'Not working', tone: 'warn' },
  ACTIVE: { word: 'Working', tone: 'ok' },
  DRAFT: { word: 'Not proved', tone: 'neutral' },
}

// A check's answer is one of three, and unproven stays its own label: folding
// it into passed is how a screen claims a register works when nobody checked.
function checkLabel(c: { ok: boolean; unproven?: boolean }): { tone: Tone; word: string } {
  if (c.unproven) return { tone: 'warn', word: 'Unproven' }
  if (c.ok) return { tone: 'ok', word: 'Passed' }
  return { tone: 'crit', word: 'Refused' }
}

// What became of a decision the register sent back. Applied moved a finding,
// unmatched found nothing to move, and the other two were kept without moving
// anything, which is neither a success nor a fault.
const OUTCOME: Record<RegisterReceipt['outcome'], { tone: Tone; word: string }> = {
  APPLIED: { tone: 'ok', word: 'Applied' },
  UNMATCHED: { tone: 'crit', word: 'Unmatched' },
  RECORDED: { tone: 'neutral', word: 'Recorded' },
  IGNORED: { tone: 'neutral', word: 'Ignored' },
}

function cardState(g: RiskRegister): string {
  if (!g.enabled) return 'PAUSED'
  if (g.probeAnswer === 'refused' || (g.lastError && g.failed > 0)) return 'FAILING'
  if (g.probeAnswer === 'ok' || (g.sent > 0 && !g.lastError)) return 'ACTIVE'
  return 'DRAFT'
}

function when(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function describe(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.issues.length > 0) return err.issues.map((i) => i.detail).join(' ')
    return err.message
  }
  return err instanceof Error ? err.message : String(err)
}

function list(words: string[]): string {
  if (words.length <= 1) return words.join('')
  return words.slice(0, -1).join(', ') + ' and ' + words[words.length - 1]
}

export function RiskRegisters({ standalone = false }: { standalone?: boolean }) {
  const [registers, setRegisters] = useState<RiskRegister[] | null>(null)
  const [ownership, setOwnership] = useState<RegisterOwnership | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [adding, setAdding] = useState(false)

  const load = useCallback(() => {
    api
      .riskRegisters()
      .then((r) => {
        setRegisters(r.registers)
        setOwnership(r.ownership)
      })
      .catch(setError)
  }, [])
  useEffect(load, [load])

  if (error != null) return <ErrorBox error={error} what="Risk registers" />
  if (!registers) return <Loading what="the registers" />

  const mayWrite = can('config.write')
  const Heading = standalone ? 'h1' : 'h2'

  return (
    <div className="settings-stack">
      <section className="panel">
        <div className="p1-head">
          <div>
            <Heading>Risk registers</Heading>
            <p className="obs-what">Where vendor risks go, and where decisions about them come back from</p>
          </div>
          <div className="p1-actions">
            <button
              type="button"
              className="button primary"
              disabled={!mayWrite}
              title={mayWrite ? undefined : refusalReason('config.write')}
              onClick={() => setAdding((v) => !v)}
              aria-expanded={adding}
            >
              Connect a register
            </button>
          </div>
        </div>
        {ownership && (
          <div className="note">
            <p className="sw-row">
              <strong>This product sends</strong> {list(ownership.tprm)}
            </p>
            <p className="sw-row">
              <strong>The register decides</strong> {list(ownership.register)}
              <Toggletip label="Why each side keeps its own part">
                Each side changes only its own part, so a decision made there is never undone by a rescore here.
              </Toggletip>
            </p>
          </div>
        )}
        {adding && (
          <RegisterForm
            onDone={() => {
              setAdding(false)
              load()
            }}
            onCancel={() => setAdding(false)}
          />
        )}
      </section>

      {registers.length === 0 && !adding && (
        <section className="panel">
          <p className="empty">No register is connected, so vendor risks stay in this product</p>
        </section>
      )}

      {registers.map((g) => (
        <RegisterCard key={g.id} register={g} onChanged={load} />
      ))}
    </div>
  )
}

function RegisterCard({ register: g, onChanged }: { register: RiskRegister; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [probe, setProbe] = useState<{ answer: string; note: string; checks: Probe[] } | null>(null)
  const [message, setMessage] = useState('')
  const [info, setInfo] = useState('')
  const [receipts, setReceipts] = useState<RegisterReceipt[] | null>(null)
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')

  const mayWrite = can('config.write')
  const mayDecide = can('risk.approve')
  const state = STATE_WORD[cardState(g)] ?? { word: 'Not proved', tone: 'neutral' }

  async function act(fn: () => Promise<void>) {
    setBusy(true)
    setMessage('')
    try {
      await fn()
    } catch (err) {
      setMessage(describe(err))
    } finally {
      setBusy(false)
    }
  }

  // Plain text is said by this screen, so it is a label with its reason behind
  // the (i). The rest are the API's own words and are shown as they came.
  const plainText = g.secretStored === 'PLAIN_TEXT'
  const problems: string[] = []
  if (g.lastError) problems.push('Sending: ' + g.lastError)
  if (g.lastReceiveError) problems.push('Coming back: ' + g.lastReceiveError)
  if (g.lastPullError) problems.push('Reading back: ' + g.lastPullError)
  if (!probe && g.probeAnswer && g.probeAnswer !== 'ok' && g.probeDetail) problems.push('Last test: ' + g.probeDetail)

  const returnAddress = g.returnPath ? window.location.origin + g.returnPath : ''

  return (
    <section className="panel">
      <div className="p1-head">
        <div>
          <h2 className="sw-title">
            {g.name} <span className="chip">{KIND_WORD[g.kind] ?? g.kind}</span>
            <Status tone={state.tone}>{state.word}</Status>
          </h2>
          <p className="muted mono" style={{ wordBreak: 'break-all' }}>
            {g.url}
          </p>
        </div>
      </div>

      <div className="std-meta">
        <span className="chip">
          <Icon name="send" size={13} /> {g.lastSentAt ? 'Last sent ' + when(g.lastSentAt) : 'Nothing sent yet'}
        </span>
        <span className="chip">{g.sent} sent</span>
        <span className={'chip' + (g.pending > 0 ? ' strong' : '')}>{g.pending} waiting</span>
        <span className={'chip' + (g.failed > 0 ? ' warn' : '')}>{g.failed} failed</span>
      </div>

      <div className="std-meta">
        {g.wayBack === 'NONE' && <span className="chip">Nothing comes back from this kind of register</span>}
        {g.wayBack === 'WEBHOOK' && (
          <span className="chip">
            <Icon name="file" size={13} />{' '}
            {g.returnSecretStored === ''
              ? 'Not set up to hear back'
              : g.lastReceivedAt
                ? 'Last heard ' + when(g.lastReceivedAt)
                : 'Nothing has come back yet'}
          </span>
        )}
        {g.wayBack === 'POLL' && (
          <span className="chip">
            <Icon name="file" size={13} /> {g.lastPulledAt ? 'Last read ' + when(g.lastPulledAt) : 'Not read yet'}
          </span>
        )}
        {g.wayBack !== 'NONE' && (
          <span className={'chip' + (g.reflectTreatment ? ' ok' : '')}>
            {g.reflectTreatment ? 'Its decisions move findings here' : 'Its decisions are recorded only'}
          </span>
        )}
      </div>

      {g.wayBack === 'WEBHOOK' && returnAddress && (
        <p className="field-note">
          Changes come back to <span className="mono" style={{ wordBreak: 'break-all' }}>{returnAddress}</span>{' '}
          <Toggletip label="How to set up the way back in Core">
            In Core, subscribe it to risk.updated, risk.closed and risk.signed_off with the full payload, then paste the
            signing secret Core shows into this register.
          </Toggletip>
        </p>
      )}

      {plainText && (
        <p className="note warn sw-row">
          <Status tone="warn">Plain text</Status>
          The key for this register is not sealed
          <Toggletip label="Why the key is in plain text">
            It is held in plain text because this deployment was started without a secret key. Set one and it is sealed
            on its own.
          </Toggletip>
        </p>
      )}
      {problems.map((p) => (
        <p key={p} className="note warn">
          {p}
        </p>
      ))}
      {message && <p className="note warn">{message}</p>}
      {info && <p className="note">{info}</p>}

      {probe && (
        <div className={'note' + (probe.answer === 'ok' ? ' ok' : probe.answer === 'refused' ? ' warn' : '')} role="status">
          <strong>{probe.note}</strong>
          <ul className="probe-list">
            {probe.checks.map((c) => {
              const label = checkLabel(c)
              return (
                <li key={c.check} className={c.unproven ? 'probe-open' : c.ok ? 'probe-ok' : 'probe-no'}>
                  <Status tone={label.tone}>{label.word}</Status>
                  <span>
                    <strong>{c.check}</strong> {c.detail}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <div className="p1-actions">
        <button
          type="button"
          className="button"
          disabled={busy || !mayWrite}
          onClick={() =>
            void act(async () => {
              const r = await api.testRiskRegister(g.id)
              setProbe({ answer: r.answer, note: r.note, checks: r.checks })
              onChanged()
            })
          }
          data-action="tprm.risk-sinks,settings-registers.a01"
        >
          {busy ? 'Working' : 'Test'}
        </button>
        <button type="button" className="button" disabled={busy || !mayWrite} onClick={() => setEditing((v) => !v)}>
          Change
        </button>
        <button
          type="button"
          className="button"
          disabled={busy || !mayWrite}
          onClick={() =>
            void act(async () => {
              const r = await api.resyncRiskRegister(g.id)
              setInfo(r.queued + (r.queued === 1 ? ' risk' : ' risks') + ' queued to go again')
              onChanged()
            })
          }
          data-action="tprm.risk-sinks,settings-registers.a02"
        >
          Send everything again
        </button>
        {g.wayBack !== 'NONE' && (
          <button
            type="button"
            className="button"
            disabled={busy}
            onClick={() =>
              void act(async () => {
                if (receipts) {
                  setReceipts(null)
                  return
                }
                const r = await api.registerReceipts(g.id)
                setReceipts(r.receipts)
              })
            }
          >
            {receipts ? 'Hide what came back' : 'What came back'}
          </button>
        )}
        {g.wayBack !== 'NONE' && (
          <button
            type="button"
            className="button ghost"
            disabled={busy || !mayDecide}
            title={mayDecide ? undefined : refusalReason('risk.approve')}
            onClick={() => {
              if (g.reflectTreatment) {
                void act(async () => {
                  await api.setRegisterTreatment(g.id, false, '')
                  onChanged()
                })
              } else {
                setAsking((v) => !v)
              }
            }}
            data-action="tprm.risk-sinks,settings-registers.a03"
          >
            {g.reflectTreatment ? 'Stop its decisions moving findings' : 'Let its decisions move findings'}
          </button>
        )}
        <button
          type="button"
          className="button ghost"
          disabled={busy || !mayWrite}
          onClick={() =>
            void act(async () => {
              await api.enableRiskRegister(g.id, !g.enabled)
              onChanged()
            })
          }
          data-action="tprm.risk-sinks,settings-registers.a04"
        >
          {g.enabled ? 'Pause' : 'Resume'}
        </button>
      </div>

      {asking && (
        <form
          className="wizard-form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            void act(async () => {
              await api.setRegisterTreatment(g.id, true, reason)
              setAsking(false)
              setReason('')
              onChanged()
            })
          }}
        >
          <p className="note warn sw-row">
            <Status tone="warn">Skips approval</Status>
            A risk accepted in {g.name} will be accepted here too
            <Toggletip label="What is skipped">
              It is accepted without this product&rsquo;s own approval chain.
            </Toggletip>
          </p>
          <label>
            Why
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} required minLength={10} rows={3} />
            <span className="field-note">Say why that is right, it is kept with the decision</span>
          </label>
          <div className="p1-actions">
            <button type="submit" className="button" disabled={busy} data-action="tprm.risk-sinks,settings-registers.a05">
              Confirm
            </button>
            <button type="button" className="button ghost" onClick={() => setAsking(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {receipts && (
        <div className="note">
          {receipts.length === 0 ? (
            <p>Nothing has come back from this register yet</p>
          ) : (
            <ul className="probe-list">
              {receipts.map((x) => (
                <li
                  key={x.deliveryId}
                  className={x.outcome === 'APPLIED' ? 'probe-ok' : x.outcome === 'UNMATCHED' ? 'probe-no' : 'probe-open'}
                >
                  <Status tone={OUTCOME[x.outcome]?.tone ?? 'neutral'}>{OUTCOME[x.outcome]?.word ?? x.outcome}</Status>
                  <span>
                    <strong>{when(x.receivedAt)}</strong> {x.externalId ? x.externalId + ': ' : ''}
                    {x.detail}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {editing && (
        <RegisterForm
          existing={g}
          onDone={() => {
            setEditing(false)
            onChanged()
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </section>
  )
}

// help is the one line under a field; tip is the reason or the detail that
// opens beside it, for a field whose whole explanation would not fit a line.
type FieldDef = {
  key: string
  label: string
  secret?: boolean
  help?: string
  tip?: { label: string; text: string }
  config?: boolean
  options?: string[]
}

const FIELDS: Record<RegisterKind, FieldDef[]> = {
  FOCTTA_CORE: [
    { key: 'name', label: 'Name' },
    { key: 'url', label: 'Inbound address', help: 'Shown by Core when inbound delivery was switched on for its risk connector' },
    { key: 'secret', label: 'Inbound secret', secret: true, help: 'Shown once by Core beside the address' },
    {
      key: 'returnSecret',
      label: 'Signing secret for changes coming back',
      secret: true,
      help: "From Core's subscription for this register, leave it empty to only send",
      tip: {
        label: 'Where the signing secret comes from',
        text: 'From the subscription created in Core for this register. It starts with whsec_.',
      },
    },
  ],
  GENERIC_WEBHOOK: [
    { key: 'name', label: 'Name' },
    { key: 'url', label: 'Where to send' },
    { key: 'keyHeader', label: 'Header the key goes in', config: true, help: 'Usually X-Api-Key' },
    { key: 'secret', label: 'Key', secret: true },
  ],
  SERVICENOW_IRM: [
    { key: 'name', label: 'Name' },
    { key: 'url', label: 'Instance address', help: 'https://yourcompany.service-now.com' },
    { key: 'authMode', label: 'How it signs in', config: true, options: ['basic', 'oauth'] },
    { key: 'username', label: 'Integration account', config: true, help: 'For basic sign-in, it needs the risk manager role' },
    { key: 'clientId', label: 'OAuth client id', config: true, help: 'For OAuth sign-in' },
    { key: 'secret', label: 'Password or client secret', secret: true },
    { key: 'table', label: 'Risk table', config: true, help: 'sn_risk_risk unless your instance keeps risks elsewhere' },
    {
      key: 'correlationField',
      label: 'Field that carries our reference',
      config: true,
      help: 'correlation_id unless you added another',
      tip: { label: 'Why this field matters', text: 'It is what stops a risk being filed twice.' },
    },
    { key: 'likelihoodField', label: 'Likelihood field', config: true, help: 'Optional' },
    { key: 'impactField', label: 'Impact field', config: true, help: 'Optional' },
  ],
}

function RegisterForm({
  existing,
  onDone,
  onCancel,
}: {
  existing?: RiskRegister
  onDone: () => void
  onCancel: () => void
}) {
  const [kind, setKind] = useState<RegisterKind>(existing?.kind ?? 'FOCTTA_CORE')
  const [values, setValues] = useState<Record<string, string>>(() => {
    const start: Record<string, string> = {}
    if (existing) {
      start.name = existing.name
      start.url = existing.url
      for (const [k, v] of Object.entries(existing.config ?? {})) start[k] = String(v ?? '')
    }
    return start
  })
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')

  async function save(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setProblem('')
    const body: RegisterInput = { kind, config: {} }
    for (const f of FIELDS[kind]) {
      const v = (values[f.key] ?? '').trim()
      if (f.config) {
        if (v) body.config![f.key] = v
        continue
      }
      if (f.secret && !v) continue
      ;(body as Record<string, unknown>)[f.key] = v
    }
    try {
      if (existing) await api.updateRiskRegister(existing.id, body)
      else await api.createRiskRegister(body)
      onDone()
    } catch (err) {
      setProblem(describe(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="wizard-form" onSubmit={(e) => void save(e)}>
      {!existing && (
        <div className="chiprow" role="group" aria-label="Kind of register">
          {(Object.keys(KIND_WORD) as RegisterKind[]).map((k) => (
            <button key={k} type="button" className="chip-toggle" aria-pressed={kind === k} onClick={() => setKind(k)}>
              {KIND_WORD[k]}
            </button>
          ))}
        </div>
      )}
      {FIELDS[kind].map((f) => {
        const held =
          existing &&
          f.secret &&
          ((f.key === 'secret' && existing.secretStored !== undefined) ||
            (f.key === 'returnSecret' && existing.returnSecretStored !== ''))
        return (
          <label key={f.key}>
            {f.label}
            {f.options ? (
              <select
                value={values[f.key] ?? f.options[0]}
                onChange={(e) => setValues((old) => ({ ...old, [f.key]: e.target.value }))}
              >
                {f.options.map((o) => (
                  <option key={o} value={o}>
                    {o === 'basic' ? 'User name and password' : 'OAuth client'}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={f.secret ? 'password' : 'text'}
                autoComplete="off"
                value={values[f.key] ?? ''}
                placeholder={held ? 'Held. Type a new one to replace it.' : undefined}
                required={!existing && (f.key === 'name' || f.key === 'url' || f.key === 'secret')}
                onChange={(e) => setValues((old) => ({ ...old, [f.key]: e.target.value }))}
              />
            )}
            {f.secret && (
              <span className="field-note">Stored encrypted with this deployment&rsquo;s key and never shown again</span>
            )}
            {f.help && (
              <span className="field-note">
                {f.help}
                {f.tip && (
                  <>
                    {' '}
                    <Toggletip label={f.tip.label}>{f.tip.text}</Toggletip>
                  </>
                )}
              </span>
            )}
          </label>
        )
      })}
      {problem && <p className="note warn">{problem}</p>}
      <div className="p1-actions">
        <button
          type="submit"
          className="button"
          disabled={busy}
          data-action="tprm.risk-sinks,settings-registers.a06"
          data-action-also="tprm.risk-sinks,settings-registers.a07"
        >
          {busy ? 'Saving' : existing ? 'Save' : 'Connect'}
        </button>
        <button type="button" className="button ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}
